import { useScanner } from '../../hooks/scanner.hook'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getContainerFromScan } from 'client/scanner/scanner.client'
import { getContainersInPreStorage } from './Storage.client'
import { toast } from 'react-toastify'
import { type ContainerWithAlerts } from './Storage.types'
import { useAtom } from 'jotai'
import { productsInContainerAtom, selectedContainerAtom } from './Storage.state'
import { useGoToMoveOptionsNavigator } from './move-options/MoveOptions.navigator'
import { getProducts } from 'client/inventory/inventory.client'
import { findProductsInfo } from 'client/products/products.client'
import { type ProductWithStock } from 'types/model'

export const useStorageHomepage = () => {
  const { t } = useTranslation('global')
  const moveOptionsNavigator = useGoToMoveOptionsNavigator()
  const { keys, getScan } = useScanner()
  const [containers, setContainers] = useState<ContainerWithAlerts[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [, setSelectedContainer] = useAtom(selectedContainerAtom)
  const [, setProducts] = useAtom(productsInContainerAtom)

  const getContainers = async () => {
    setIsLoading(true)
    try {
      const containers = await getContainersInPreStorage()
      setContainers(containers)
    } finally {
      setIsLoading(false)
    }
  }

  // On init
  useEffect(() => {
    getContainers().catch((e) => {
      toast.error((e as Error).message)
    })
  }, [])

  // On scan
  useEffect(() => {
    const scanned = getScan()
    if (scanned) {
      setIsLoading(true)
      getContainerFromScan(scanned)
        .then((c) => {
          if (c.location.type !== 'PreStorage') {
            toast.error(t('storage-home.invalid-scan', { scanned }))
            return
          }
          setSelectedContainer(c)
          getProducts(c.container.name)
            .then(async (products) => {
              if (products.length > 0) {
                const res = await findProductsInfo(products.map((p) => p.productEan))
                const mappedProducts = res.map((it): ProductWithStock => {
                  const stock = products.find((p) => p.productEan === it.ean)?.stock ?? 0
                  return { ...it, stock }
                })
                setProducts(mappedProducts)
                moveOptionsNavigator.go(c)
              } else {
                toast.error(t('alerts.scan-product.error-no-products'))
              }
            })
            .catch((err: Error) => {
              toast.error(err.message)
            })
            .finally(() => {
              setIsLoading(false)
            })
        })
        .catch(() => {
          toast.error(t('storage-container-detail.bad-request', { scanned }))
        })
        .finally(() => { setIsLoading(false) })
    }
  }, [keys, getScan])

  return {
    containers,
    isLoading
  }
}
